import { DataTable } from "mantine-datatable";
import { graphql } from "gql.tada";
import {
  TournamentAdmin,
  TournamentAdminPermissions,
} from "../../../../gql/graphql";
import { AutoDataTable } from "../../components/AutoDataTable";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { Button, Checkbox, MultiSelect, Stack } from "@mantine/core";
import { modals } from "@mantine/modals";
import { PlayerSearch } from "../../components/form/PlayerSearch";
import { useState } from "react";

const TournamentAdminsQuery = graphql(`
  query tournamentAdmins($tournamentId: ID!) {
    tournamentAdmins(tournamentId: $tournamentId) {
      tournamentId
      permissions
      account {
        id
        email
        profiles {
          username
        }
      }
    }
  }
`);

const TournamentAdminDeleteMutation = graphql(`
  mutation deleteTournamentAdmin($tournamentId: ID!, $accountId: ID!) {
    tournamentAdminDelete(
      input: { tournamentId: $tournamentId, accountId: $accountId }
    )
  }
`);

const TournamentAdminUpsertMutation = graphql(`
  mutation tournamentAdminUpsert(
    $tournamentId: ID!
    $accountId: ID!
    $permissions: [TournamentAdminPermissions!]!
  ) {
    tournamentAdminUpsert(
      input: {
        tournamentId: $tournamentId
        accountId: $accountId
        permissions: $permissions
      }
    ) {
      accountId
    }
  }
`);

const AddAdminModal = ({ tournamentId }: { tournamentId: string }) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const [selectionHasOwner, setSelectionHasOwner] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState<
    TournamentAdminPermissions[]
  >([]);
  const [upsert] = useMutation<any>(TournamentAdminUpsertMutation);

  return (
    <>
      <PlayerSearch
        error={
          selectionHasOwner
            ? undefined
            : "This player has no linked account and cannot be made admin"
        }
        onChange={(value) => {
          setSelectedAccountId(value.ownerId ?? undefined);
          setSelectionHasOwner(value.ownerId !== null);
        }}
      />
      <MultiSelect
        data={Object.values(TournamentAdminPermissions)}
        onChange={(value) => {
          setSelectedPermissions(value as TournamentAdminPermissions[]);
        }}
      />
      <Button
        onClick={() =>
          upsert({
            variables: {
              tournamentId,
              accountId: selectedAccountId ?? "",
              permissions: selectedPermissions,
            },
          })
        }
        disabled={!selectedAccountId || selectedPermissions.length === 0}
      >
        Add admin
      </Button>
    </>
  );
};

export const TournamentAdmins = () => {
  const tournamentId = useParams().tournamentId;
  const navigate = useNavigate();

  return (
    <Stack gap={0}>
      <Button
        w={200}
        onClick={() =>
          modals.open({
            title: "New admin",
            children: <AddAdminModal tournamentId={tournamentId ?? ""} />,
            onClose() {
              navigate(0);
            },
          })
        }
      >
        New admin
      </Button>
      <AutoDataTable
        query={TournamentAdminsQuery}
        deleteMutation={TournamentAdminDeleteMutation}
        queryVariables={{
          tournamentId,
        }}
        createButtonText={false}
        columns={[
          {
            title: "Name/ID",
            accessor: "name",
            rawElement: (row: TournamentAdmin) =>
              row.account.profiles[0]?.username ??
              row.account.email ??
              row.account.id,
          },
          {
            title: "Permissions",
            accessor: "permissions",
            rawElement: (row: TournamentAdmin) => row.permissions.join(", "),
          },
        ]}
      />
    </Stack>
  );
};
